import moment from 'moment';
import {
  getCheckInBookings,
  getCheckOutBookings,
  getDashboardPendingTasks,
  getNoShowBookings,
  getRoomCleanings,
  getMaintenanceRequests,
} from '../services/api';
import {
  formatHotelDate,
  formatHotelDateTime,
} from './dateUtils';
import {
  formatBookingCurrency,
  normalizeBookingStatusValue,
  resolveBookingPaymentDisplay,
  BOOKING_PAYMENT_TOLERANCE,
  roundBookingMoney,
} from './bookingOpsCardHelpers';
import {
  DASHBOARD_PENDING_TASKS_TTL_MS,
  fetchWithDashboardCache,
  getDashboardPendingTasksCacheKey,
  invalidateDashboardDataCache,
  readDashboardCache,
} from './dashboardDataCache';
import { dashboardNeedsRefresh } from './dashboardRefreshCoordinator';

const PENDING_CHECKIN_STATUSES = new Set(['confirmed', 'reserved', 'pending', 'partial_checkin']);
const IN_HOUSE_CHECKOUT_STATUSES = new Set(['checked_in', 'partial_checkin', 'partial_checkout']);

export function isOtaBooking(booking) {
  const channelType = String(booking?.channel_data?.channel_type || '').toUpperCase();
  const channelName = String(booking?.channel_data?.name || '').toLowerCase().trim();
  const source = String(booking?.booking_source || '').toLowerCase().trim();

  if (channelType && !['DIRECT', 'PMS', 'WALK_IN', 'WALK-IN'].includes(channelType)) {
    return true;
  }
  if (channelName && !['direct', 'pms', 'walk-in', 'walkin'].includes(channelName)) {
    return true;
  }
  if (source && !['direct', 'pms', 'walk-in', 'walkin', '—', '-', ''].includes(source)) {
    return true;
  }
  return false;
}

function formatRooms(booking) {
  const rooms = booking?.allocated_rooms || [];
  if (rooms.length > 0) {
    const nums = rooms.map((r) => r.room_number).filter(Boolean);
    if (nums.length) return nums.join(', ');
  }
  return booking?.rooms ? `${booking.rooms} room${booking.rooms > 1 ? 's' : ''}` : '';
}

function bookingSubtitle(booking) {
  const code = booking?.code ? `#${booking.code}` : '';
  const rooms = formatRooms(booking);
  return [code, rooms].filter(Boolean).join(' · ');
}

function isPendingCheckout(booking, todayStr) {
  if (booking?.is_pending_checkout !== undefined && booking?.is_pending_checkout !== null) {
    return Boolean(booking.is_pending_checkout);
  }
  const checkoutDate = booking?.check_out_date;
  if (!checkoutDate) return false;
  return checkoutDate < todayStr || checkoutDate === todayStr;
}

function isPaymentPending(booking) {
  return resolveTaskPayment(booking).isDue;
}

function resolveTaskPayment(booking) {
  const payment = resolveBookingPaymentDisplay(booking);
  const apiRemaining = roundBookingMoney(
    booking?.remaining_amount ?? booking?.payment_summary?.remaining_balance,
  );

  if (apiRemaining > BOOKING_PAYMENT_TOLERANCE) {
    const totalPaid = roundBookingMoney(
      booking?.total_paid ?? booking?.payment_summary?.total_paid ?? payment.totalPaid,
    );
    const paymentStatus = totalPaid > BOOKING_PAYMENT_TOLERANCE
      ? 'partial_payment'
      : (booking?.payment_status === 'partial_payment' ? 'partial_payment' : 'due');

    return {
      ...payment,
      totalPaid,
      remainingAmount: apiRemaining,
      paymentStatus,
      isDue: true,
      showPaidBreakdown: totalPaid > BOOKING_PAYMENT_TOLERANCE,
      showDueBreakdown: true,
    };
  }

  return payment;
}

function formatDueLabel(booking) {
  const payment = resolveTaskPayment(booking);
  if (!payment.isDue) return '';
  const amount = formatBookingCurrency(payment.remainingAmount, payment.currency);
  if (payment.paymentStatus === 'partial_payment') {
    return `${amount} partial due`;
  }
  return `${amount} due`;
}

function formatCheckinDueBadge(booking) {
  const payment = resolveTaskPayment(booking);
  if (!payment.isDue) return '';
  const amount = formatBookingCurrency(payment.remainingAmount, payment.currency);
  if (payment.paymentStatus === 'partial_payment') {
    return `${amount} · Partial`;
  }
  return amount;
}

function appendDueMeta(baseMeta, booking) {
  const dueLabel = formatDueLabel(booking);
  if (!dueLabel) return baseMeta;
  return baseMeta ? `${baseMeta} · ${dueLabel}` : dueLabel;
}

function mapBookingTask(booking, section, options = {}) {
  const {
    meta = '',
    badgeLabel = '',
    badgeColor = '#94a3b8',
    sortKey = 0,
  } = options;

  return {
    id: `${section}-${booking.id}`,
    section,
    bookingId: booking.id,
    title: booking.guest_name || 'Guest',
    subtitle: bookingSubtitle(booking),
    meta,
    badgeLabel,
    badgeColor,
    channelName: booking?.channel_data?.name || booking?.booking_source || 'Direct',
    sortKey,
  };
}

function resolveRoomId(ref) {
  if (ref == null || ref === '') return null;
  if (typeof ref === 'object') return ref.id ?? null;
  return ref;
}

function mapCleaningTask(cleaning) {
  const room =
    typeof cleaning.room === 'object' && cleaning.room != null
      ? cleaning.room
      : { room_number: cleaning.room_number, id: cleaning.room_id ?? cleaning.room };
  const roomLabel = room?.room_number ? `Room ${room.room_number}` : 'Room';
  const statusLabel = cleaning.status === 'IN_PROGRESS' ? 'In progress' : 'Pending';

  const cleaningTypeLabels = {
    STAYOVER: 'Stay Over',
    CHECKOUT: 'Check Out',
    DEEP: 'Deep Clean',
  };
  const cleaningTypeLabel =
    cleaning.cleaning_type_display
    || cleaningTypeLabels[cleaning.cleaning_type]
    || cleaning.cleaning_type
    || 'Room cleaning';
  const cleaningDateLabel = cleaning.cleaning_date
    ? moment(cleaning.cleaning_date).format('DD MMM YYYY')
    : '';

  return {
    id: `housekeeping-cleaning-${cleaning.id}`,
    section: 'housekeeping',
    bookingId: null,
    cleaningId: cleaning.id,
    roomId: resolveRoomId(room) ?? resolveRoomId(cleaning.room_id ?? cleaning.room),
    title: roomLabel,
    subtitle: cleaningTypeLabel,
    meta: cleaningDateLabel ? `${cleaningDateLabel} · ${statusLabel}` : statusLabel,
    badgeLabel: statusLabel,
    badgeColor: '#ffb74d',
    sortKey: moment(cleaning.cleaning_date || undefined).valueOf() || 0,
  };
}

function mapMaintenanceTask(request) {
  const room =
    typeof request.room === 'object' && request.room != null
      ? request.room
      : { room_number: request.room_number, id: request.room_id ?? request.room };
  const roomLabel = room?.room_number ? `Room ${room.room_number}` : 'Maintenance';
  const priority = String(request.priority || 'NORMAL').toUpperCase();
  const reportedDateLabel = request.reported_date
    ? moment(request.reported_date).format('DD MMM YYYY')
    : '';

  return {
    id: `housekeeping-maintenance-${request.id}`,
    section: 'housekeeping',
    bookingId: null,
    maintenanceId: request.id,
    roomId: room?.id ?? request.room_id ?? null,
    title: roomLabel,
    subtitle: request.issue_type_display || request.issue_type || 'Maintenance request',
    meta: request.description
      ? String(request.description).slice(0, 60)
      : (reportedDateLabel ? `${reportedDateLabel} · Open` : 'Open request'),
    badgeLabel: priority,
    badgeColor: priority === 'HIGH' || priority === 'URGENT' ? '#ef5350' : '#ffb74d',
    sortKey: priority === 'HIGH' || priority === 'URGENT' ? 0 : priority === 'MEDIUM' ? 1 : 2,
  };
}

export async function fetchDashboardPendingTasks({ forceRefresh = false } = {}) {
  const todayStr = moment().format('YYYY-MM-DD');
  const cacheKey = getDashboardPendingTasksCacheKey(todayStr);

  try {
    const bundle = await fetchWithDashboardCache(
      cacheKey,
      () => getDashboardPendingTasks(todayStr, { refresh: forceRefresh }),
      { ttl: DASHBOARD_PENDING_TASKS_TTL_MS, forceRefresh },
    );
    return mapDashboardPendingTasksFromBundle(bundle, todayStr);
  } catch {
    invalidateDashboardDataCache('pending-tasks:');
    return fetchDashboardPendingTasksLegacy();
  }
}

function mapWebCheckInTask(invite) {
  const roomNumber = invite?.room?.number || invite?.room?.room_number;
  const roomType = invite?.room?.room_type_name || '';
  const roomLabel = roomNumber
    ? `Room ${roomNumber}${roomType ? ` · ${roomType}` : ''}`
    : (roomType || 'Room pending');
  const stay = [
    invite?.check_in_date ? formatHotelDate(invite.check_in_date) : null,
    invite?.check_out_date ? formatHotelDate(invite.check_out_date) : null,
  ].filter(Boolean).join(' → ');
  const expiresLabel = invite?.expires_at
    ? `Expires ${formatHotelDateTime(invite.expires_at)}`
    : 'Link active';

  return {
    id: `web-checkin-${invite.id}`,
    section: 'web_checkin',
    bookingId: null,
    inviteId: invite.id,
    invite,
    title: invite?.guest_name || 'Guest',
    subtitle: [roomLabel, stay].filter(Boolean).join(' · '),
    meta: expiresLabel,
    badgeLabel: 'Pending',
    badgeColor: '#12b886',
    channelName: 'Web Check-in',
    sortKey: invite?.expires_at ? moment(invite.expires_at).valueOf() : 0,
  };
}

function mapDashboardPendingTasksFromBundle(bundle, todayStr) {
  const checkoutRes = bundle?.checkout || { bookings: [] };
  const checkinRes = bundle?.checkin || { bookings: [] };
  const paymentDueRes = bundle?.payment_due || { bookings: [] };
  const noShowRes = bundle?.no_show || { bookings: [] };
  const webCheckinRes = bundle?.web_checkin || { invites: [] };
  const cleaningsRes = bundle?.cleanings || { results: [] };
  const maintenanceRes = bundle?.maintenance || { results: [] };

  return buildDashboardPendingTasksResult({
    todayStr,
    checkoutRes,
    checkinRes,
    paymentDueRes,
    noShowRes,
    webCheckinRes,
    cleaningsRes,
    maintenanceRes,
  });
}

async function fetchDashboardPendingTasksLegacy() {
  const todayStr = moment().format('YYYY-MM-DD');

  const [checkoutRes, checkinRes, noShowRes, cleaningsRes, maintenanceRes] = await Promise.all([
    getCheckOutBookings(todayStr, { includePendingCheckout: true, pendingLookbackDays: 14 }).catch(() => ({ bookings: [] })),
    getCheckInBookings(todayStr).catch(() => ({ bookings: [] })),
    getNoShowBookings(todayStr).catch(() => ({ bookings: [] })),
    getRoomCleanings({ status: 'PENDING', page_size: 500 }).catch(() => ({ results: [] })),
    getMaintenanceRequests({ status: 'OPEN', page_size: 200 }).catch(() => ({ results: [] })),
  ]);

  return buildDashboardPendingTasksResult({
    todayStr,
    checkoutRes,
    checkinRes,
    paymentDueRes: { bookings: [] },
    noShowRes,
    webCheckinRes: { invites: [] },
    cleaningsRes,
    maintenanceRes,
  });
}

function buildDashboardPendingTasksResult({
  todayStr,
  checkoutRes,
  checkinRes,
  paymentDueRes,
  noShowRes,
  webCheckinRes,
  cleaningsRes,
  maintenanceRes,
}) {

  const checkoutBookings = (checkoutRes?.bookings || []).filter(
    (b) => IN_HOUSE_CHECKOUT_STATUSES.has(normalizeBookingStatusValue(b)),
  );

  const checkoutTasks = checkoutBookings.map((booking) => {
    const status = normalizeBookingStatusValue(booking);
    const overdue = isPendingCheckout(booking, todayStr) && booking.check_out_date < todayStr;
    const partial = status === 'partial_checkout' || status === 'partial_checkin';
    let meta = 'Checkout today';
    let badgeLabel = 'Today';
    let badgeColor = '#4ce1b6';

    if (overdue) {
      meta = `Due ${moment(booking.check_out_date).format('DD MMM')}`;
      badgeLabel = 'Overdue';
      badgeColor = '#ef5350';
    } else if (status === 'partial_checkout') {
      meta = `Partial checkout · ${moment(booking.check_out_date).format('DD MMM')}`;
      badgeLabel = 'Partial';
      badgeColor = '#ffb74d';
    } else if (status === 'partial_checkin') {
      meta = 'Partial check-in · checkout today';
      badgeLabel = 'Partial';
      badgeColor = '#ffb74d';
    }

    return mapBookingTask(booking, 'checkout', {
      meta: appendDueMeta(meta, booking),
      badgeLabel: (() => {
        const dueLabel = formatDueLabel(booking);
        if (dueLabel) {
          return formatBookingCurrency(
            resolveTaskPayment(booking).remainingAmount,
            booking.currency || 'INR',
          );
        }
        return badgeLabel;
      })(),
      badgeColor: isPaymentPending(booking) ? '#f59e0b' : badgeColor,
      sortKey: overdue ? 0 : partial ? 1 : 2,
    });
  });

  const checkinBookings = (checkinRes?.bookings || []).filter((booking) => {
    const status = normalizeBookingStatusValue(booking);
    if (['checked_in', 'checked_out', 'cancelled', 'canceled', 'no_show'].includes(status)) {
      return false;
    }
    return PENDING_CHECKIN_STATUSES.has(status);
  });

  const checkinTasks = checkinBookings.map((booking) => {
    const status = normalizeBookingStatusValue(booking);
    const payment = resolveTaskPayment(booking);
    const isPartialCheckin = status === 'partial_checkin';
    const arrival = booking.check_in_date
      ? moment(booking.check_in_date).format('DD MMM')
      : 'Today';
    const isOverdueArrival = booking.check_in_date && booking.check_in_date < todayStr;
    const arrivalMeta = isPartialCheckin
      ? `Partial check-in · ${arrival}`
      : (isOverdueArrival ? `Overdue arrival · ${arrival}` : `Arrival ${arrival}`);
    const dueBadge = formatCheckinDueBadge(booking);

    return mapBookingTask(booking, 'checkin', {
      meta: appendDueMeta(arrivalMeta, booking),
      badgeLabel: dueBadge || (isPartialCheckin ? 'Partial' : (isOverdueArrival ? 'Overdue' : 'Arrival')),
      badgeColor: payment.isDue ? '#f59e0b' : (isPartialCheckin || isOverdueArrival ? '#ffb74d' : '#ff9800'),
      sortKey: payment.isDue ? -payment.remainingAmount : (isOverdueArrival ? 0 : (isPartialCheckin ? 1 : 2)),
    });
  });

  const paymentBookingMap = new Map();
  const registerPaymentDueBooking = (booking) => {
    if (!booking?.id || !isPaymentPending(booking)) return;
    paymentBookingMap.set(booking.id, booking);
  };

  checkoutBookings.forEach(registerPaymentDueBooking);
  checkinBookings.forEach(registerPaymentDueBooking);
  (paymentDueRes?.bookings || []).forEach(registerPaymentDueBooking);

  const paymentTasks = Array.from(paymentBookingMap.values()).map((booking) => {
    const payment = resolveTaskPayment(booking);
    const formatted = formatBookingCurrency(payment.remainingAmount, payment.currency);
    const partialSuffix = payment.paymentStatus === 'partial_payment' ? ' partial' : '';
    const status = normalizeBookingStatusValue(booking);
    const context = PENDING_CHECKIN_STATUSES.has(status)
      ? 'Pre-arrival'
      : (IN_HOUSE_CHECKOUT_STATUSES.has(status) ? 'In-house' : '');

    return mapBookingTask(booking, 'payment', {
      meta: context
        ? `${formatted}${partialSuffix} due · ${context}`
        : `${formatted}${partialSuffix} due`,
      badgeLabel: payment.paymentStatus === 'no_payment' || payment.paymentStatus === 'due'
        ? 'Unpaid'
        : 'Balance',
      badgeColor: '#f59e0b',
      sortKey: payment.remainingAmount,
    });
  }).sort((a, b) => b.sortKey - a.sortKey);

  const cleanings = (cleaningsRes?.results || cleaningsRes || [])
    .filter((c) => String(c.status || '').toUpperCase() === 'PENDING');

  const maintenance = (maintenanceRes?.results || maintenanceRes || [])
    .filter((r) => String(r.status || '').toUpperCase() === 'OPEN');

  const housekeepingTasks = [
    ...cleanings.map(mapCleaningTask),
    ...maintenance.map(mapMaintenanceTask),
  ].sort((a, b) => a.sortKey - b.sortKey);

  const noShowTasks = (noShowRes?.bookings || [])
    .filter((booking) => booking.no_show_status === 'pending' && isOtaBooking(booking))
    .map((booking) => mapBookingTask(booking, 'no_show_ota', {
      meta: booking.no_show_ota?.needs_retry || booking.no_show_ota?.success === false
        ? `OTA failed · retry no-show on ${booking.no_show_ota?.partner_label || booking.channel_data?.name || 'OTA'}`
        : `Mark no-show · ${booking.channel_data?.name || 'OTA'}`,
      badgeLabel: booking.no_show_ota?.needs_retry ? 'Retry OTA' : 'OTA',
      badgeColor: booking.no_show_ota?.needs_retry ? '#ef5350' : '#a78bfa',
      sortKey: booking.check_out_date || todayStr,
    }));

  const webCheckinTasks = (webCheckinRes?.invites || [])
    .filter((invite) => invite?.id)
    .map(mapWebCheckInTask)
    .sort((a, b) => a.sortKey - b.sortKey);

  const sections = [
    {
      key: 'no_show_ota',
      label: 'No Show',
      items: noShowTasks,
    },
    {
      key: 'web_checkin',
      label: 'Web Checkin',
      items: webCheckinTasks,
    },
    {
      key: 'checkin',
      label: 'Checkin',
      items: checkinTasks.sort((a, b) => a.sortKey - b.sortKey),
    },
    {
      key: 'payment',
      label: 'Payment',
      items: paymentTasks,
    },
    {
      key: 'checkout',
      label: 'Checkout',
      items: checkoutTasks.sort((a, b) => a.sortKey - b.sortKey),
    },
    {
      key: 'housekeeping',
      label: 'Housekeeping',
      items: housekeepingTasks,
    },
  ];

  const totalCount = sections.reduce((sum, section) => sum + section.items.length, 0);

  return {
    sections,
    totalCount,
    fetchedAt: new Date().toISOString(),
    pendingCleaningByRoomId: buildPendingCleaningByRoomId(cleaningsRes),
  };
}

/** Single fetch for dashboard page: tasks rail + room cleaning badges. */
export async function fetchDashboardPendingTasksBundle({ forceRefresh = false } = {}) {
  const tasks = await fetchDashboardPendingTasks({ forceRefresh });
  return {
    tasks,
    pendingCleaningByRoomId: tasks.pendingCleaningByRoomId || {},
  };
}

export function readCachedPendingTasksBundle() {
  if (dashboardNeedsRefresh()) {
    return null;
  }
  const todayStr = moment().format('YYYY-MM-DD');
  const cacheKey = getDashboardPendingTasksCacheKey(todayStr);
  const raw = readDashboardCache(cacheKey);
  if (!raw) return null;

  const tasks = mapDashboardPendingTasksFromBundle(raw, todayStr);
  return {
    tasks,
    pendingCleaningByRoomId: tasks.pendingCleaningByRoomId || {},
  };
}

export function invalidateDashboardPendingTasksCache() {
  invalidateDashboardDataCache('pending-tasks:');
}

/** room id (string) -> open cleaning record id (in-progress preferred) */
export function buildPendingCleaningByRoomId(cleaningsRes) {
  const cleanings = (cleaningsRes?.results || cleaningsRes || []).filter((cleaning) => {
    const status = String(cleaning.status || '').toUpperCase();
    return status === 'PENDING' || status === 'IN_PROGRESS';
  });
  const map = {};
  cleanings.forEach((cleaning) => {
    const task = mapCleaningTask(cleaning);
    if (task.roomId == null || task.roomId === '') return;
    const key = String(task.roomId);
    const currentId = map[key];
    if (!currentId || String(cleaning.status || '').toUpperCase() === 'IN_PROGRESS') {
      map[key] = Number(cleaning.id);
    }
  });
  return map;
}

export async function fetchPendingCleaningByRoomId({ forceRefresh = false } = {}) {
  const bundle = await fetchDashboardPendingTasksBundle({ forceRefresh });
  return bundle.pendingCleaningByRoomId;
}
